import type { ReactNode, CSSProperties } from 'react';

interface ServiceCardProps {
  /** Emoji or icon shown in the leading rounded square */
  emoji?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  onClick?: () => void;
  /** Right-side content shown before the chevron — badges, prices, etc. */
  trailing?: ReactNode;
  /** Hide the chevron (e.g. for non-navigable summary cards) */
  noChevron?: boolean;
  /** Optional left-border accent colour (e.g. kitchen station colour) */
  accentColor?: string;
  /** Extra className tacked onto the root */
  className?: string;
  /** Optional inline style overrides */
  style?: CSSProperties;
  /** Render as a non-clickable div instead of a button */
  as?: 'button' | 'div';
}

/**
 * The canonical clickable list-card from Welcome page — emoji square +
 * title + description + chevron. Used across modules for service entries
 * and any "tap to open" row.
 */
export default function ServiceCard({
  emoji,
  title,
  description,
  onClick,
  trailing,
  noChevron,
  accentColor,
  className,
  style,
  as,
}: ServiceCardProps) {
  const Element: 'button' | 'div' = as ?? (onClick ? 'button' : 'div');
  const isInteractive = Element === 'button' || !!onClick;
  const accentStyle: CSSProperties = accentColor ? { borderLeft: `4px solid ${accentColor}` } : {};

  const baseClass = [
    'card mb-3 shadow-sm service-card-item w-100 text-start border-0',
    isInteractive ? '' : 'card-static',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Element
      className={baseClass}
      style={{ cursor: isInteractive ? 'pointer' : 'default', ...accentStyle, ...style }}
      onClick={onClick}
    >
      <div className="card-body d-flex align-items-center">
        {emoji && <span className="service-card-emoji">{emoji}</span>}
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <h6 className="card-title mb-1" style={{ fontWeight: 600 }}>{title}</h6>
          {description && <p className="text-muted small mb-0">{description}</p>}
        </div>
        {trailing && <div className="service-card-trailing">{trailing}</div>}
        {!noChevron && isInteractive && (
          <svg
            className="service-card-chevron"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        )}
      </div>
    </Element>
  );
}
